import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { FC, FocusEvent, MouseEvent, MutableRefObject, Ref } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil'
import type { BaseItem } from '@messenger/types'
import {
  canvasPageIdAtom,
  editorPagesAtom,
  pageSetLocaleAtom,
  selectionIsSoleSelector,
  showServiceTemplatesSwapMenuAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import {
  showConvertTextModalForItemIdAtom,
  showServiceTemplateModalForItemIdAtom,
  swapServiceTemplateForItemIdAtom,
} from '@/atoms/itemControlAtoms'
import { caseAtom } from '@/atoms/caseAtoms'
import { selectedThemeAtom } from '@/atoms/printPackageAtoms'
import { dateFormatAtom } from '@/atoms/settingsAtoms'
import {
  editorPageContentAtomFamily,
  editorRoleDecorationsSelector,
  editorTextBoxDocumentsSelector,
  editorTextBoxDraftSelector,
  editorTextBoxDraftsAtom,
  editorTextBoxSeedIdsAtom,
} from '@/atoms/editorTextBoxAtoms'
import { getEditorPageContent } from '@/api/editorPageContent'
import { setEditorThemeRoleDecoration } from '@/api/editorCascade'
import {
  useAuthorCaseField,
  useAuthorCaseFields,
  useAuthorCaseRows,
} from '@/hooks/editor/useAuthorCaseField'
import { useBleedWarning } from '@/hooks/editor/useBleedWarning'
import type { LoadResultForWarning } from '@/hooks/editor/useBleedWarning'
import { useEditorTextBoxGrowthCeiling } from '@/hooks/editor/useEditorTextBoxGrowthCeiling'
import { logTextBoxLoadResult } from '@/utils/editor/textBoxLoadLog'
import { textBoxSeedFrameHeightPx } from '@/utils/editor/textBoxSeedFrame'
import type {
  EditorGrowthCeiling,
  EditorTextBoxPageGeometry,
} from '@/hooks/editor/useEditorTextBoxGrowthCeiling'
import { rememberEditorTextBoxInkRect } from '@/utils/editor/measureEditorTextBoxInkBounds'
import {
  reportPreviewBleedWarning,
  reportPreviewLoadWarning,
} from '@/utils/editor/previewBleedReport'
import type { EditorTextBoxBleedReport } from '@/utils/editor/previewBleedReport'
import { useEditorServiceTemplateCreate } from '@/hooks/editor/useEditorServiceTemplateCreate'
import { useItemControlState } from '@/hooks/editor/useItemControlState'
import { useSyncCurrentPageToEditorPages } from '@/hooks/editor/useSyncCurrentPageToEditorPages'
import { useIsAdmin } from '@/hooks/utility/useIsAdmin'
import caseDateForPicker from '@/utils/dates/caseDateForPicker'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'
import { countEditorProbeRender } from '@/utils/editor/editorProbe'
import { roundItemSizePx } from '@/hooks/editor/useItemResize'
import {
  EDITOR_TEXT_BOX_ATTR,
  EDITOR_TEXT_FIT_SETTLED_ATTR,
  TEXT_BOUNDED_BY_ATTR,
  TEXT_BOUNDED_BY_NONE,
  TEXT_CEILING_ATTR,
  TEXT_CEILING_FROM_ATTR,
  TEXT_CONTENT_HEIGHT_ATTR,
  TEXT_LOAD_HEIGHT_ATTR,
  TEXT_MAX_SIZE_PT_ATTR,
  TEXT_SIZING_ATTR,
  itemRectAttribute,
  stampKeptCeiling,
} from '@/utils/editor/textGeometryAttributes'
import { stampEditorTextFit } from '@/utils/editor/stampEditorTextFit'
import formatDate from '@/utils/dates/formatDate'
import {
  EDITOR_BLOCK_ORDINAL_DATASET_KEY,
  EDITOR_CASE_DETAIL_TABLE_BLOCK_CLASS_NAME,
  EDITOR_EDIT_CHROME_SELECTOR,
  EDITOR_EMPTY_FIELD_ATTR,
  EDITOR_FIELD_NAME_OF_DECEASED,
  EDITOR_RUN_KIND_ATTR,
  EDITOR_RUN_ORDINAL_DATASET_KEY,
  EDITOR_TEXT_BLOCK_SELECTOR,
  EDITOR_TEXT_BOX_ITEM_TYPE,
  EDITOR_TEXT_BOX_SELECTOR,
  EDITOR_TEXT_RUN_SELECTOR,
  SERVICE_BLOCK_DATE_FORMAT,
  collectUsedFontsFromPages,
  editorCharacterLayerFromStylingInput,
  editorCharacterStylingInputFromLayer,
  EDITOR_FIT_HEIGHT_TOL_PX,
  EDITOR_TEXT_SCALING_ENABLED,
  editorFitWritesSizes,
  editorHeldFrameHeight,
  editorTextBoxFacesLoaded,
  editorTextBoxFacesWriteSizes,
  editorTextBoxFrameFollowsText,
  editorTextBoxLineHeightPx,
  ensureEditorTextBoxResolvedStyles,
  ensureEditorTextBoxStructuralSheet,
  fitRenderedEditorTextBox,
  installEditorBoxFaceRefit,
  installEditorFontRefitTrigger,
  isEditorCornerHandle,
  isEmptyEditorTextBoxDocument,
  isPlaceholderOnlyEditorTextBoxDocument,
  mountEditorTextBox,
  previewRenderedEditorTextBoxCornerScale,
  EDITOR_BOX_TEXT_SIZING_COLUMNS,
  renderEditorTextBox,
  resolveEditorTextBoxDocument,
  editorTextBoxFieldLabel,
  editorTextSizingOf,
  sameEditorTextBoxContent,
  scaleEditorTextBoxDocumentSizes,
  editorFieldOutlinePaths,
  tabularRowForm,
  toEditorTextBoxThemeColors,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorBoxLayoutDescription,
  EditorBoxLocalRect,
  EditorCharacterStyleLayer,
  EditorContentRoleValue,
  EditorFieldOutline,
  EditorFieldOutlinePad,
  EditorFitExplanation,
  EditorInkClusterBox,
  EditorInkRect,
  EditorRenderedTextBox,
  EditorSelection,
  EditorTextBoxDocument,
  EditorTextBoxMount,
  EditorTextSizing,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import ServiceTemplateCreateModal from '@/components/editor/ServiceTemplatesCustomizer/ServiceTemplateCreateModal'
import CompositeFieldModal, {
  COMPOSITE_FIELD_KEYS,
} from './CompositeFieldModal'
import type { CompositeFieldKey } from './CompositeFieldModal'
import DeceasedNameFieldModal from './DeceasedNameFieldModal'
import EditorTextBoxSizingInspector from './EditorTextBoxSizingInspector'
import EditorTextBoxToolbar from './EditorTextBoxToolbar'
import FieldInsertModal from './FieldInsertModal'
import type { FieldAddress } from './EditorTextBoxAuthoringModal'
import MakeEditableModal from './MakeEditableModal'
import TabularFieldModal from './TabularFieldModal'
import ThemeRoleDecorationModal from './ThemeRoleDecorationModal'
import * as S from './PageEditorTextBox.styled'

type PageEditorTextBoxProps = Partial<
  Pick<BaseItem, 'x' | 'y' | 'z' | 'width' | 'height' | 'edits'>
> & {
  id: string
  environment?: string
  itemRef?: Ref<HTMLDivElement>
  editorPageId?: string | null
  full?: boolean
  print?: boolean
  selected?: boolean
  multiSelectMode?: boolean
  dragDisabled?: [boolean, (next: boolean) => void]
  assetLocked?: boolean
  resizingState?: string
  resizeHandle?: string | null
  internalWidth?: [number, (next: number) => void]
  internalHeight?: [number, (next: number) => void]
  controlledY?: number
  controlledX?: number
  setItemWidth?: (next: number) => void
  setItemHeight?: (next: number) => void
  editorTextBoxMountApiRef?: MutableRefObject<EditorTextBoxMountApi | null>
  /**
   * Where the canvas publishes the height the resting paint needs at its
   * painted sizes, after every read-only fit: the floor the resize frame
   * holds a handle above while text scaling is off.
   */
  editorTextBoxTextNeedRef?: MutableRefObject<number | null>
  /**
   * Moves the item's top by `dy` page px, live position and row alike: the
   * fit that follows a north handle's release raises the frame to the lines
   * the released width wraps, and the bottom edge the handle anchored to
   * must stay where the person left it.
   */
  shiftItemY?: (dy: number) => void
  /** The preview's margins and page ids: a settled box reports its own crossing. */
  bleedReport?: EditorTextBoxBleedReport | null
  /**
   * The page as the load rule reads it, off the canvas: items, margins, page
   * size, padding. `ProductPrint` hands it to every item, both kinds of text
   * box, so the one ceiling serves both. The canvas has the editor's atoms
   * and is handed nothing; without it a box grows nothing and fits into its
   * saved frame.
   */
  pageGeometry?: EditorTextBoxPageGeometry | null
}

export type EditorTextBoxMountApi = {
  textHeightPx: () => number | null
  /** The height the text needs at the sizes the host last painted. */
  paintedHeightPx: () => number | null
}

type ProbeHostElement = HTMLDivElement & {
  __editorTextBoxMount?: EditorTextBoxMount
  __editorTextBoxGrowth?: {
    textHeightPx: number
    next: number
    live: number | undefined
  }
}

/**
 * The host's document with the text-sizing columns of `held` put back on it.
 *
 * Both come from the row, or from `useEditorSetTextBoxSizing` writing the
 * box's draft directly (the drawer's maximum field, shown only while text
 * scaling is on, and the armed e2e probe), and the editing host never authors them — it holds
 * whatever copy of the box it was mounted with. So a commit out of the host
 * carries the page's values for these two, not the host's.
 */
const withEditorTextSizingOf = (
  held: EditorTextBoxDocument['box'] | undefined,
  next: EditorTextBoxDocument,
): EditorTextBoxDocument => {
  if (held === undefined) return next
  const changed = EDITOR_BOX_TEXT_SIZING_COLUMNS.some(
    (column) => held[column] !== next.box[column],
  )
  if (!changed) return next
  const box = { ...next.box }
  for (const column of EDITOR_BOX_TEXT_SIZING_COLUMNS)
    if (held[column] === undefined) delete box[column]
    else Object.assign(box, { [column]: held[column] })
  try {
    return resolveEditorTextBoxDocument({ ...next, box })
  } catch (error) {
    console.error(
      `editor text box ${next.box.boxId}: the text sizing could not be carried across a commit — the box keeps the host's copy`,
      error,
    )
    return next
  }
}

const isDragState = (resizingState: string | undefined): boolean =>
  resizingState === 'start' || resizingState === 'resizing'
type ProbeWindow = Window & { __EDITOR_E2E_PROBE__?: boolean }

type FieldModalState = {
  fieldKey: string
  savedSel: EditorSelection | null
  serviceId?: string
  /** The additional-family group a rows modal edits; unset, every row of the field. */
  rowType?: string
  /** The run a double-click opened the modal on; the toolbar sets none. */
  field?: FieldAddress
}
type RoleModalState = {
  initialRole: EditorContentRoleValue
  savedSel: EditorSelection | null
}

const readInkDebugFlag = (): boolean => {
  try {
    if (typeof window === 'undefined') return false
    if (window.localStorage?.getItem('editorTextBoxInkDebug') === '1')
      return true
    return /[?&]editorTextBoxInkDebug=1/.test(window.location?.search ?? '')
  } catch {
    return false
  }
}

const readSizingInspectorFlag = (): boolean => {
  try {
    if (typeof window === 'undefined') return false
    if (window.localStorage?.getItem('editorTextBoxSizingInspector') === '1')
      return true
    return /[?&]editorTextBoxSizingInspector=1/.test(
      window.location?.search ?? '',
    )
  } catch {
    return false
  }
}

const isFocusExit = (e: FocusEvent<HTMLDivElement>): boolean => {
  const to = e.relatedTarget as Element | null
  const wholeTabLostActivation = !to && !window.document.hasFocus()
  if (wholeTabLostActivation) return false
  if (to && e.currentTarget.contains(to)) return false
  if (
    to?.closest?.(
      `[data-item-control-overlay], [data-resize-handle], [data-editor-text-toolbar-strip], [data-editor-text-popover], [data-editor-text-sizing-inspector], ${EDITOR_EDIT_CHROME_SELECTOR}`,
    )
  )
    return false
  return true
}

const HeldSelectionLayer: FC<{ rects: EditorBoxLocalRect[] | null }> = ({
  rects,
}) =>
  rects && rects.length > 0 ? (
    <S.SelectionHoldLayer data-editor-text-selection-hold aria-hidden="true">
      {rects.map((r, i) => (
        <S.SelectionHoldRect
          key={i}
          style={{
            top: `${r.top}px`,
            left: `${r.left}px`,
            width: `${r.width}px`,
            height: `${r.height}px`,
          }}
        />
      ))}
    </S.SelectionHoldLayer>
  ) : null

const InkDebugLayer: FC<{ clusters: EditorInkClusterBox[] | null }> = ({
  clusters,
}) =>
  clusters && clusters.length > 0 ? (
    <S.InkDebugLayer data-editor-text-ink-debug aria-hidden="true">
      {clusters.map((c, i) => (
        <S.InkClusterOutline
          key={i}
          title={c.char}
          style={{
            top: `${c.top}px`,
            left: `${c.left}px`,
            width: `${c.right - c.left}px`,
            height: `${c.bottom - c.top}px`,
          }}
        />
      ))}
    </S.InkDebugLayer>
  ) : null

const ProtectPulseLayer: FC<{
  pulse: { animationRestartKey: number; rects: EditorBoxLocalRect[] } | null
}> = ({ pulse }) =>
  pulse && pulse.rects.length > 0 ? (
    <S.ProtectPulseLayer data-editor-text-role-target-pulse aria-hidden="true">
      {pulse.rects.map((r, i) => (
        <S.ProtectPulseRect
          key={`${pulse.animationRestartKey}-${i}`}
          style={{
            top: `${r.top}px`,
            left: `${r.left}px`,
            width: `${r.width}px`,
            height: `${r.height}px`,
          }}
        />
      ))}
    </S.ProtectPulseLayer>
  ) : null

type FieldTooltipState = { fieldKey: string; left: number; top: number }

type FieldRunHit = FieldAddress & { el: HTMLElement }

const blockOrdinalOf = (el: HTMLElement): number =>
  Number(
    el.closest<HTMLElement>(EDITOR_TEXT_BLOCK_SELECTOR)?.dataset[
      EDITOR_BLOCK_ORDINAL_DATASET_KEY
    ],
  )

/**
 * The field run a pointer event landed on inside `within`, with its
 * ordinals. A cell of a case-detail table counts as the table's first run:
 * every cell is the same field, its rows are edited as one, and the table
 * paints a cell for every row of the case whether or not the box holds a
 * run for it.
 */
const fieldRunUnder = (
  target: EventTarget | null,
  within: HTMLElement | null,
): FieldRunHit | null => {
  const node = target as Element | null
  const el = node?.closest?.<HTMLElement>(
    `${EDITOR_TEXT_RUN_SELECTOR}[${EDITOR_RUN_KIND_ATTR}="field"]`,
  )
  if (el && within?.contains(el)) {
    const blockIndex = blockOrdinalOf(el)
    const runIndex = Number(el.dataset[EDITOR_RUN_ORDINAL_DATASET_KEY])
    if (!Number.isInteger(blockIndex) || !Number.isInteger(runIndex))
      return null
    return { el, blockIndex, runIndex }
  }
  const cell = node?.closest?.<HTMLElement>(
    `.${EDITOR_CASE_DETAIL_TABLE_BLOCK_CLASS_NAME} td`,
  )
  if (!cell || !within?.contains(cell)) return null
  const blockIndex = blockOrdinalOf(cell)
  if (!Number.isInteger(blockIndex)) return null
  return {
    el: cell.querySelector<HTMLElement>(EDITOR_TEXT_RUN_SELECTOR) ?? cell,
    blockIndex,
    runIndex: 0,
  }
}

const useFieldTooltip = (getMount: () => EditorTextBoxMount | null) => {
  const [fieldTooltip, setFieldTooltip] = useState<FieldTooltipState | null>(
    null,
  )

  const handleEditingMouseOver = (e: MouseEvent<HTMLDivElement>) => {
    const hit = fieldRunUnder(e.target, e.currentTarget)
    if (!hit || hit.el.hasAttribute(EDITOR_EMPTY_FIELD_ATTR)) {
      setFieldTooltip((prev) => (prev === null ? prev : null))
      return
    }
    const { el, blockIndex: bi, runIndex: ri } = hit
    const key = getMount()?.fieldRunAt?.(bi, ri)?.fieldKey ?? ''
    const gRect = el.getBoundingClientRect()
    // The tip is positioned in viewport px: it portals to document.body.
    const tip: FieldTooltipState = {
      fieldKey: key,
      left: gRect.left + gRect.width / 2,
      top: gRect.top,
    }
    setFieldTooltip((prev) =>
      prev &&
      prev.fieldKey === tip.fieldKey &&
      prev.left === tip.left &&
      prev.top === tip.top
        ? prev
        : tip,
    )
  }

  const clearFieldTooltip = useCallback(() => setFieldTooltip(null), [])

  return { fieldTooltip, handleEditingMouseOver, clearFieldTooltip }
}

const sameFieldOutlines = (
  a: EditorFieldOutline[],
  b: EditorFieldOutline[],
): boolean =>
  a.length === b.length &&
  a.every((entry, i) => {
    const other = b[i]
    return (
      entry.blockIndex === other.blockIndex &&
      entry.runIndex === other.runIndex &&
      entry.fieldKey === other.fieldKey &&
      entry.lines.length === other.lines.length &&
      entry.lines.every(
        (line, j) =>
          line.top === other.lines[j].top &&
          line.left === other.lines[j].left &&
          line.width === other.lines[j].width &&
          line.height === other.lines[j].height,
      )
    )
  })

/**
 * The lines of each group of dynamic text — a filled field run, a
 * case-detail table with a filled cell — while the box is edited, read from
 * the mount after every refit, on the live read's interval, and once a
 * frame after editing begins: the mount's own first fit refits before the
 * handle is held. A held resize handle reflows the paint without refitting,
 * so no lines are held until the release refits.
 */
const useFieldOutlines = (
  getMount: () => EditorTextBoxMount | null,
  editing: boolean,
  dragging: boolean,
  subscribeRefit: (listener: () => void) => () => void,
) => {
  const [fieldOutlines, setFieldOutlines] = useState<
    EditorFieldOutline[] | null
  >(null)
  const draggingRef = useRef(dragging)
  draggingRef.current = dragging

  const readFieldOutlines = useCallback(() => {
    if (draggingRef.current) return
    const next = getMount()?.fieldOutlines() ?? null
    setFieldOutlines((prev) =>
      prev !== null && next !== null && sameFieldOutlines(prev, next)
        ? prev
        : next,
    )
  }, [getMount])

  useEffect(() => {
    if (!editing || dragging) {
      setFieldOutlines(null)
      return undefined
    }
    const unsubscribe = subscribeRefit(readFieldOutlines)
    const raf = requestAnimationFrame(readFieldOutlines)
    return () => {
      unsubscribe()
      cancelAnimationFrame(raf)
    }
  }, [editing, dragging, subscribeRefit, readFieldOutlines])

  return { fieldOutlines: dragging ? null : fieldOutlines, readFieldOutlines }
}

/**
 * How far outside a group's lines a ring's path runs: 2 px of air plus half
 * its 2 px stroke, so the stroke starts 2 px out from the glyphs across and
 * 1 px above the first line box and below the last. At a leading under
 * about 1.3, where glyph boxes hang past their line boxes, the stroke
 * touches the descenders of the line above it and the capitals of the line
 * below it: at the 1.0 floor those are less than a stroke apart, so no
 * placement clears both.
 */
const FIELD_OUTLINE_PAD: EditorFieldOutlinePad = { x: 3, y: 2 }

/**
 * The dashed rings around every group of dynamic text: one per field run,
 * following its lines, and one per case-detail table around the whole
 * table; a run whose consecutive lines share no horizontal span wears one
 * ring per line, the two keeping to the edge where the lines meet.
 */
const FieldOutlineLayer: FC<{
  outlines: EditorFieldOutline[] | null
}> = ({ outlines }) =>
  outlines && outlines.length > 0 ? (
    <S.FieldOutlineLayer aria-hidden="true">
      {outlines.flatMap(({ blockIndex, runIndex, fieldKey, lines }) =>
        editorFieldOutlinePaths(lines, FIELD_OUTLINE_PAD).map((d, i) => (
          <S.FieldOutline
            key={`${blockIndex}:${runIndex}:${i}`}
            data-editor-text-field-outline={fieldKey}
            d={d}
          />
        )),
      )}
    </S.FieldOutlineLayer>
  ) : null

const FieldTooltipPortal: FC<{ tooltip: FieldTooltipState | null }> = ({
  tooltip,
}) => {
  const { t } = useTranslation()
  return tooltip
    ? createPortal(
        <S.FieldTooltip
          data-editor-text-field-tooltip
          role="tooltip"
          style={{ left: `${tooltip.left}px`, top: `${tooltip.top}px` }}
        >
          {editorTextBoxFieldLabel(t, tooltip.fieldKey)}
        </S.FieldTooltip>,
        document.body,
      )
    : null
}

const PageEditorTextBox = ({
  id,
  environment,
  itemRef,
  editorPageId,
  full,
  print,
  selected = false,
  multiSelectMode = false,
  dragDisabled: [, setDragDisabled] = [false, () => {}],
  assetLocked = false,
  resizingState,
  resizeHandle = null,
  internalWidth,
  internalHeight,
  controlledY,
  controlledX,
  setItemWidth,
  setItemHeight,
  editorTextBoxMountApiRef,
  editorTextBoxTextNeedRef,
  shiftItemY,
  bleedReport = null,
  pageGeometry = null,
  x,
  y,
  z,
  width,
  height,
  edits,
}: PageEditorTextBoxProps) => {
  countEditorProbeRender('PageEditorTextBox')
  useEditorProbeLifetime('PageEditorTextBox')
  const isEditor = environment === 'editor'
  const canvasPageId = useRecoilValue(canvasPageIdAtom)
  const soleSelection = useRecoilValue(selectionIsSoleSelector)
  const pageId = editorPageId ?? canvasPageId ?? ''
  const results = useRecoilValue(editorTextBoxDocumentsSelector(pageId))
  const result = results?.get(id) ?? null
  const draft = useRecoilValue(editorTextBoxDraftSelector(id))
  const setDrafts = useSetRecoilState(editorTextBoxDraftsAtom)
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)
  // A draft is the editor's alone: the preview and the proof paint the rows,
  // so words a user walked away from without saving never reach them.
  const textBoxDocument: EditorTextBoxDocument | null =
    (isEditor ? draft : null) ?? result?.document ?? null

  const [editRequested, setEditRequested] = useState(false)
  const [confirmingMakeEditable, setConfirmingMakeEditable] = useState(false)
  const [makeEditableArmed, setMakeEditableArmed] = useState(false)
  const editing =
    isEditor &&
    selected &&
    !assetLocked &&
    soleSelection &&
    editRequested &&
    textBoxDocument !== null

  useEffect(() => {
    if (selected && soleSelection) return
    setEditRequested(false)
    setConfirmingMakeEditable(false)
    setMakeEditableArmed(false)
  }, [selected, soleSelection])

  const requestEditing = useCallback(() => {
    if (!isEditor || assetLocked || multiSelectMode) return
    setEditRequested(true)
  }, [isEditor, assetLocked, multiSelectMode])

  const [mountFailed, setMountFailed] = useState(false)
  const hostElRef = useRef<HTMLDivElement | null>(null)
  const readonlyHostRef = useRef<HTMLDivElement | null>(null)
  const mountRef = useRef<EditorTextBoxMount | null>(null)
  const mountedFromRef = useRef<EditorTextBoxDocument | null>(null)

  const dragging = isDragState(resizingState)
  const liveWidth = Array.isArray(internalWidth)
    ? internalWidth[0]
    : (width ?? 0)
  const liveHeight = Array.isArray(internalHeight)
    ? internalHeight[0]
    : (height ?? 0)
  const setLiveWidth = Array.isArray(internalWidth)
    ? internalWidth[1]
    : undefined
  const setLiveHeight = Array.isArray(internalHeight)
    ? internalHeight[1]
    : undefined
  const liveHeightRef = useRef(liveHeight)
  liveHeightRef.current = liveHeight
  const liveWidthRef = useRef(liveWidth)
  liveWidthRef.current = liveWidth

  // The growth probe for the e2e specs: the text height the mount measured
  // against the frame the page holds, stamped on every refit. The mount
  // itself grows the frame on the author's own edits and hands the new
  // frame to applyFrameFromModel.
  const stampGrowthProbe = useCallback(() => {
    const mount = mountRef.current
    if (!mount) return
    const textHeightPx = mount.textHeightPx()
    if (
      textHeightPx == null ||
      !Number.isFinite(textHeightPx) ||
      textHeightPx <= 0
    )
      return
    const next = Math.ceil(textHeightPx - 1e-6)
    countEditorProbeRender(
      `growth:${id.slice(0, 8)}:${next}:live=${liveHeightRef.current}`,
    )
    if ((window as ProbeWindow).__EDITOR_E2E_PROBE__ && hostElRef.current)
      (hostElRef.current as ProbeHostElement).__editorTextBoxGrowth = {
        textHeightPx,
        next,
        live: liveHeightRef.current,
      }
  }, [id])

  // The frame the mount holds, echoed to the page: growth raising the
  // height on the author's own edit, or an undo restoring a frame.
  // The height the last frame change applied, until the next render carries
  // it in `liveHeight`: two changes in one tick measure the second's shift
  // from the first's height, not from the height the render still shows.
  const appliedHeightRef = useRef<number | null>(null)
  useEffect(() => {
    appliedHeightRef.current = null
  }, [liveHeight])
  // The handle a release on the read-only paint let go of, until the fit
  // that follows the release has run: the raise that fit adds anchors to
  // that handle's edge, as the editing host's release does through its own
  // record. The render that ends a drag sets it, a re-render before the fit
  // (the chrome settling back to idle) keeps it, and the fit consumes it.
  const releasedHandleRef = useRef<string | null>(null)
  // The settle that follows a release can raise the frame to the lines the
  // released width wraps; under a north handle that raise keeps the bottom
  // edge the handle anchored to, so the top moves up by it. Every other
  // frame change (a held handle, whose anchor the resize chrome keeps;
  // typing; the drawer) leaves the position where it is.
  const applyFrameFromModel = useCallback(
    (nextWidth: number, nextHeight: number, anchor?: string) => {
      const width = Math.round(nextWidth)
      const height = Math.round(nextHeight)
      if (width > 0) {
        setLiveWidth?.(width)
        setItemWidth?.(width)
      }
      if (height > 0) {
        if (anchor?.includes('n')) {
          const from = appliedHeightRef.current ?? liveHeightRef.current
          if (from !== height) shiftItemY?.(from - height)
        }
        appliedHeightRef.current = height
        setLiveHeight?.(height)
        setItemHeight?.(height)
      }
    },
    [setLiveWidth, setItemWidth, setLiveHeight, setItemHeight, shiftItemY],
  )

  // The host's document — text, sizes and the frame it holds — is the draft
  // the page paints, committed after every fit the host runs, so the paint
  // that follows editing (by any exit: a blur, a toolbar action that moves
  // the selection, a page turn) fits the document the host last painted and
  // never a stale one. The host's frame is the truth: the page hears every
  // growth through onFrameChange, and hands the host every resize.
  const commitDraft = useCallback(
    (mount: EditorTextBoxMount, of: 'live' | 'settled' = 'settled') => {
      const from = mountedFromRef.current
      const next = of === 'live' ? mount.liveDocument() : mount.document()
      countEditorProbeRender(
        `commitDraft:${id.slice(0, 8)}:h=${next.box.height}:same=${from !== null && sameEditorTextBoxContent(from, next)}`,
      )
      if (from !== null && sameEditorTextBoxContent(from, next)) return
      setDrafts((prev) => {
        // The drawer's columns are the page's, not the host's: the drawer
        // writes them straight into this draft while the box may be mounted,
        // and the host holds the copy of the box it was mounted with. Without
        // this carry the host's next commit — the person's first keystroke —
        // would put its older copy of them back, and the mode the designer
        // just chose would vanish as they typed.
        const carried = withEditorTextSizingOf(prev[id]?.box, next)
        mountedFromRef.current = carried
        return { ...prev, [id]: carried }
      })
      setUnsavedChanges(true)
    },
    [id, setDrafts, setUnsavedChanges],
  )

  const getMount = useCallback(() => mountRef.current, [])
  const getHostEl = useCallback(() => hostElRef.current, [])
  const { fieldTooltip, handleEditingMouseOver, clearFieldTooltip } =
    useFieldTooltip(getMount)
  useEffect(() => {
    if (!editing) clearFieldTooltip()
  }, [editing, clearFieldTooltip])

  const refitListenersRef = useRef<Set<() => void>>(new Set())
  const subscribeRefit = useCallback((listener: () => void) => {
    refitListenersRef.current.add(listener)
    return () => {
      refitListenersRef.current.delete(listener)
    }
  }, [])
  const { fieldOutlines, readFieldOutlines } = useFieldOutlines(
    getMount,
    editing,
    dragging,
    subscribeRefit,
  )

  const [heldSelection, setHeldSelection] = useState<
    EditorBoxLocalRect[] | null
  >(null)
  useEffect(() => {
    if (!editing) setHeldSelection(null)
  }, [editing])

  const [protectPulse, setProtectPulse] = useState<{
    animationRestartKey: number
    rects: EditorBoxLocalRect[]
  } | null>(null)
  const protectPulseKeyRef = useRef(0)
  const protectPulseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  const pulseRoleTarget = useCallback((rects: EditorBoxLocalRect[]) => {
    if (!rects || rects.length === 0) return
    protectPulseKeyRef.current += 1
    setProtectPulse({ animationRestartKey: protectPulseKeyRef.current, rects })
    if (protectPulseTimerRef.current) clearTimeout(protectPulseTimerRef.current)
    protectPulseTimerRef.current = setTimeout(() => setProtectPulse(null), 600)
  }, [])
  useEffect(() => {
    if (!editing) setProtectPulse(null)
  }, [editing])
  useEffect(
    () => () => {
      if (protectPulseTimerRef.current)
        clearTimeout(protectPulseTimerRef.current)
    },
    [],
  )

  const selectedTheme = useRecoilValue(selectedThemeAtom) as Record<
    string,
    unknown
  > | null
  const themeColors = useMemo(
    () => toEditorTextBoxThemeColors(selectedTheme),
    [selectedTheme],
  )
  const decorations = useRecoilValue(editorRoleDecorationsSelector(pageId))

  // The separator is a rule block inside the box, at the caret, as wide as
  // the box's text; the box stays in editing with the caret on the line after
  // it.
  const insertRuleAtCaret = useCallback(() => {
    mountRef.current?.insertRule()
  }, [])

  const scanUsedFonts = useRecoilCallback(
    ({ snapshot }) =>
      () => {
        const pages = snapshot.getLoadable(editorPagesAtom).getValue() as
          | Parameters<typeof collectUsedFontsFromPages>[0]
          | null
        const pageResults = snapshot
          .getLoadable(editorTextBoxDocumentsSelector(pageId))
          .getValue()
        const drafts = snapshot
          .getLoadable(editorTextBoxDraftsAtom)
          .getValue() as Record<string, EditorTextBoxDocument>
        const byId = new Map<string, EditorTextBoxDocument>()
        if (pageResults)
          for (const viewModel of pageResults.values())
            if (viewModel.document)
              byId.set(viewModel.boxId, viewModel.document)
        for (const [boxId, boxDraft] of Object.entries(drafts))
          byId.set(boxId, boxDraft)
        const live = mountRef.current?.document()
        if (live) byId.set(id, live)
        return collectUsedFontsFromPages(pages ?? [], [...byId.values()])
      },
    [id, pageId],
  )

  const isAdmin = useIsAdmin()

  const {
    templateName: serviceTemplateName,
    setTemplateName: setServiceTemplateName,
    showModal: showServiceTemplateModal,
    setShowModal: setShowServiceTemplateModal,
    createTemplateFromBlocks,
  } = useEditorServiceTemplateCreate()
  const [
    showServiceTemplateModalForItemId,
    setShowServiceTemplateModalForItemId,
  ] = useRecoilState(showServiceTemplateModalForItemIdAtom)
  const [swapServiceTemplateForItemId, setSwapServiceTemplateForItemId] =
    useRecoilState(swapServiceTemplateForItemIdAtom)
  const [showConvertTextModalForItemId, setShowConvertTextModalForItemId] =
    useRecoilState(showConvertTextModalForItemIdAtom)
  const setShowServiceTemplatesSwapMenu = useSetRecoilState(
    showServiceTemplatesSwapMenuAtom,
  )

  useEffect(() => {
    if (showServiceTemplateModalForItemId !== id) return
    setShowServiceTemplateModal(true)
    setShowServiceTemplateModalForItemId(null)
  }, [
    showServiceTemplateModalForItemId,
    id,
    setShowServiceTemplateModal,
    setShowServiceTemplateModalForItemId,
  ])

  useEffect(() => {
    if (swapServiceTemplateForItemId !== id) return
    setShowServiceTemplatesSwapMenu(true)
    setSwapServiceTemplateForItemId(null)
  }, [
    swapServiceTemplateForItemId,
    id,
    setShowServiceTemplatesSwapMenu,
    setSwapServiceTemplateForItemId,
  ])

  useEffect(() => {
    if (showConvertTextModalForItemId !== id) return
    setConfirmingMakeEditable(true)
    setShowConvertTextModalForItemId(null)
  }, [showConvertTextModalForItemId, id, setShowConvertTextModalForItemId])

  const cancelMakeEditable = useCallback(
    () => setConfirmingMakeEditable(false),
    [],
  )

  // The freeze runs through the live mount so it reuses the editor's history
  // sealing, which merges every field into one undo step; a box that is only
  // selected has no mount yet, so it enters editing first and converts once
  // the mount exists.
  const confirmMakeEditable = useCallback(() => {
    setConfirmingMakeEditable(false)
    setMakeEditableArmed(true)
    setEditRequested(true)
  }, [])

  const handleCreateServiceTemplate = useCallback(() => {
    const blocks =
      mountRef.current?.document().box.blocks ??
      textBoxDocument?.box.blocks ??
      []
    void createTemplateFromBlocks(blocks)
  }, [createTemplateFromBlocks, textBoxDocument])

  const [fieldModal, setFieldModal] = useState<FieldModalState | null>(null)
  const [roleModal, setRoleModal] = useState<RoleModalState | null>(null)
  const openConfigureField = useCallback(
    (
      fieldKey: string,
      savedSel: EditorSelection | null,
      serviceId?: string,
      rowType?: string,
    ) => setFieldModal({ fieldKey, savedSel, serviceId, rowType }),
    [],
  )
  // A double-click on a field run while editing opens that run's modal in
  // its edit mode — the modal the toolbar's configure button opens, on the
  // run under the pointer; a cell of a case-detail table opens the rows
  // modal of its table. Off a field, or before editing, the double-click
  // is the gesture that enters editing.
  // Where the double-click that opens the box landed: the caret goes there
  // once the host mounts, so the word under the pointer is the word a size
  // command sizes, as it is in the TinyMCE editor.
  const entryPointRef = useRef<{ clientX: number; clientY: number } | null>(
    null,
  )
  const handleDoubleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!editing) {
        entryPointRef.current = { clientX: e.clientX, clientY: e.clientY }
        requestEditing()
        return
      }
      const hit = fieldRunUnder(e.target, hostElRef.current)
      if (!hit) return
      const mount = getMount()
      const run = mount?.fieldRunAt?.(hit.blockIndex, hit.runIndex)
      if (!run) return
      e.preventDefault()
      clearFieldTooltip()
      setFieldModal({
        fieldKey: run.fieldKey,
        savedSel: mount?.getModelSelection?.() ?? null,
        serviceId: run.params?.serviceId,
        rowType: run.params?.rowType,
        field: { blockIndex: hit.blockIndex, runIndex: hit.runIndex },
      })
    },
    [editing, requestEditing, getMount, clearFieldTooltip],
  )
  const openRoleModal = useCallback(
    (initialRole: EditorContentRoleValue, savedSel: EditorSelection | null) =>
      setRoleModal({ initialRole, savedSel }),
    [],
  )
  useEffect(() => {
    if (!editing) {
      setFieldModal(null)
      setRoleModal(null)
    }
  }, [editing])

  const onAuthorValue = useAuthorCaseField()
  const onAuthorValues = useAuthorCaseFields()
  const onAuthorRows = useAuthorCaseRows()

  const caseDetails = useRecoilValue(caseAtom) as Record<string, unknown> | null
  const dateFormat = useRecoilValue(dateFormatAtom) as string
  const pageSetLocale = useRecoilValue(pageSetLocaleAtom)
  const dateSupport = useMemo(
    () => ({
      getRawDate: (column: string) => caseDateForPicker(caseDetails?.[column]),
      formatDate: (date: Date | null) =>
        date ? formatDate(date, dateFormat, false, pageSetLocale) : '',
      getServiceRawDate: (serviceId: string) => {
        const services = caseDetails?.services
        const row = Array.isArray(services)
          ? (services as { id?: unknown; date?: unknown }[]).find(
              (service) => service?.id === serviceId,
            )
          : undefined
        return caseDateForPicker(row?.date)
      },
      formatServiceDate: (date: Date | null) =>
        date
          ? formatDate(date, SERVICE_BLOCK_DATE_FORMAT, true, pageSetLocale)
          : '',
    }),
    [caseDetails, dateFormat, pageSetLocale],
  )

  const modalCaseData =
    result?.document?.context.caseData ?? textBoxDocument?.context.caseData

  const decorationLayers = useMemo(() => {
    const refs = result?.document?.context.refs
    if (!refs) return {}
    const layers: Partial<
      Record<EditorContentRoleValue, EditorCharacterStyleLayer>
    > = {}
    for (const [role, input] of Object.entries(decorations)) {
      layers[role as EditorContentRoleValue] =
        editorCharacterLayerFromStylingInput(input, refs)
    }
    return layers
  }, [decorations, result])

  const selectedThemeId = (selectedTheme as { id?: string } | null)?.id
  const syncCurrentPageToEditorPages = useSyncCurrentPageToEditorPages()
  const onAuthorDecoration = useRecoilCallback(
    ({ set }) =>
      async (
        role: EditorContentRoleValue,
        character: EditorCharacterStyleLayer,
      ): Promise<boolean> => {
        if (!selectedThemeId || !pageId) return false
        try {
          await setEditorThemeRoleDecoration(
            selectedThemeId,
            role,
            editorCharacterStylingInputFromLayer(character),
          )
          const { data: bundle } = await getEditorPageContent(pageId)
          // The bundle swap re-runs the canvas hydration, which rebuilds
          // the items from the page JSON, so an item the JSON has not
          // heard of yet is dropped unless the canvas is synced in first.
          await syncCurrentPageToEditorPages()
          set(editorPageContentAtomFamily(pageId), bundle)
          return true
        } catch (error) {
          console.error(
            `PageEditorTextBox: box ${id} — the role decoration could not be saved`,
            error,
          )
          return false
        }
      },
    [selectedThemeId, pageId, id, syncCurrentPageToEditorPages],
  )

  const lastContextRef = useRef<unknown>(null)
  useEffect(() => {
    if (!editing) {
      lastContextRef.current = null
      return
    }
    const context = result?.document?.context
    if (!context) return
    if (lastContextRef.current === null) {
      lastContextRef.current = context
      return
    }
    if (lastContextRef.current === context) return
    lastContextRef.current = context
    mountRef.current?.setContext(context)
  }, [editing, result])

  // The two text-sizing columns, into the host that is already mounted. The
  // drawer shows on the selection an entered box still is, so its maximum
  // can change mid-edit, and the armed e2e probe can write either column
  // then; the host holds the copy of the box it mounted with, and without
  // this it would keep fitting under the old values while the draft said
  // otherwise. `setTextSizing` repaints under `hold`: no frame
  // moves, nothing is written, no history step.
  const sizing = textBoxDocument?.box.sizing
  const maxFontSizePt = textBoxDocument?.box.maxFontSizePt
  useEffect(() => {
    if (!editing) return
    mountRef.current?.setTextSizing(sizing, maxFontSizePt)
  }, [editing, sizing, maxFontSizePt])

  const sizingInspector = useMemo(
    () => isEditor && readSizingInspectorFlag(),
    [isEditor],
  )
  const [inspection, setInspection] = useState<{
    fit: EditorFitExplanation | null
    layout: EditorBoxLayoutDescription | null
  } | null>(null)
  const inspectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (!editing || !sizingInspector) {
      if (inspectTimerRef.current) clearTimeout(inspectTimerRef.current)
      setInspection(null)
      return undefined
    }
    const refresh = () => {
      if (inspectTimerRef.current) clearTimeout(inspectTimerRef.current)
      inspectTimerRef.current = setTimeout(() => {
        const mount = mountRef.current
        if (!mount) return
        setInspection({
          fit: mount.fitExplanation(),
          layout: mount.describeBoxLayout(),
        })
      }, 160)
    }
    const unsubscribe = subscribeRefit(refresh)
    const raf = requestAnimationFrame(refresh)
    return () => {
      unsubscribe()
      cancelAnimationFrame(raf)
      if (inspectTimerRef.current) clearTimeout(inspectTimerRef.current)
    }
  }, [editing, sizingInspector, subscribeRefit])

  useEffect(() => {
    setDragDisabled(editing)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  useEffect(() => {
    if (!editing || !hostElRef.current || textBoxDocument === null) return
    let mount: EditorTextBoxMount
    try {
      mount = mountEditorTextBox(hostElRef.current, textBoxDocument, {
        ...(liveWidthRef.current > 0 && liveHeightRef.current > 0
          ? {
              frame: {
                width: liveWidthRef.current,
                height: liveHeightRef.current,
              },
            }
          : {}),
        onRefit: () => {
          if (hostElRef.current) stampFit(hostElRef.current)
          stampGrowthProbe()
          // The mount's own first fit runs before the handle exists; the
          // cleanup below commits whatever the last fit left.
          if (mountRef.current) commitDraft(mountRef.current, 'live')
          refitListenersRef.current.forEach((listener) => listener())
        },
        onDragStateChange: (nowDragging: boolean) => {
          if (nowDragging && hostElRef.current)
            boxRootOf(hostElRef.current)?.removeAttribute(
              EDITOR_TEXT_FIT_SETTLED_ATTR,
            )
          refitListenersRef.current.forEach((listener) => listener())
        },
        onFrameChange: applyFrameFromModel,
        isStylingChrome: (el: Element) =>
          !!el.closest?.(
            `[data-item-control-overlay], [data-editor-text-toolbar-strip], [data-editor-text-popover], [data-editor-text-sizing-inspector], ${EDITOR_EDIT_CHROME_SELECTOR}`,
          ),
      })
    } catch (err) {
      console.error(
        `PageEditorTextBox: live mount failed for box ${id}:`,
        err instanceof Error ? err.message : String(err),
      )
      setMountFailed(true)
      return
    }
    setMountFailed(false)
    mountRef.current = mount
    mountedFromRef.current = textBoxDocument
    const probeHostEl = hostElRef.current as ProbeHostElement
    if ((window as ProbeWindow).__EDITOR_E2E_PROBE__)
      probeHostEl.__editorTextBoxMount = mount
    stampGrowthProbe()
    stampFit(hostElRef.current)
    hostElRef.current.focus()
    const entryPoint = entryPointRef.current
    entryPointRef.current = null
    if (entryPoint)
      mount.placeCaretAtClientPoint(entryPoint.clientX, entryPoint.clientY)
    return () => {
      commitDraft(mount)
      delete probeHostEl.__editorTextBoxMount
      mountRef.current = null
      mountedFromRef.current = null
      mount.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing])

  const savedRevision = textBoxDocument?.revision
  useEffect(() => {
    const mount = mountRef.current
    if (!editing || mount === null || savedRevision === undefined) return
    if (mount.document().revision === savedRevision) return
    mount.setRevision(savedRevision)
    const from = mountedFromRef.current
    if (from !== null)
      mountedFromRef.current = { ...from, revision: savedRevision }
  }, [editing, savedRevision])

  useEffect(() => {
    if (!makeEditableArmed) return
    const mount = mountRef.current
    if (!editing || mount === null) return
    mount.convertAllFieldsToText()
    commitDraft(mount)
    setMakeEditableArmed(false)
  }, [makeEditableArmed, editing, commitDraft])

  useEffect(() => {
    if (!editorTextBoxMountApiRef) return undefined
    editorTextBoxMountApiRef.current = editing
      ? {
          textHeightPx: () => mountRef.current?.textHeightPx() ?? null,
          paintedHeightPx: () => mountRef.current?.paintedHeightPx() ?? null,
        }
      : null
    return () => {
      if (editorTextBoxMountApiRef) editorTextBoxMountApiRef.current = null
    }
  }, [editing, editorTextBoxMountApiRef])

  // The drag state on the editing surface: while a handle is held the frame
  // follows the pointer and the mount fits the paint only (a pill) or
  // previews authored × k (a corner); the release hands the mount the fixed
  // frame for one full fit that writes. Outside a drag the frame the page
  // holds (growth echoing back, or a size typed into the drawer) reaches the
  // mount.
  const dragHandleRef = useRef<string | null>(null)
  useEffect(() => {
    if (!editing) return
    const mount = mountRef.current
    if (!mount) return
    if (dragging) {
      if (dragHandleRef.current === null) {
        dragHandleRef.current = resizeHandle ?? 'se'
        mount.beginResize(
          dragHandleRef.current as Parameters<typeof mount.beginResize>[0],
        )
      }
      // The fit sees the frame on the whole-pixel grid the row holds, so the
      // frame it measures while the pill is held is the frame the release
      // writes (`roundItemSizePx`, the commit's own rounding). The drag's own
      // geometry keeps its sub-pixel precision: rounding the live size at its
      // source moves the anchored edge with it.
      if (liveWidth > 0 && liveHeight > 0)
        mount.resizeTo(roundItemSizePx(liveWidth), roundItemSizePx(liveHeight))
      return
    }
    if (dragHandleRef.current !== null) {
      dragHandleRef.current = null
      if (liveWidth > 0 && liveHeight > 0)
        mount.endResize(roundItemSizePx(liveWidth), roundItemSizePx(liveHeight))
      return
    }
    if (liveWidth > 0 && liveHeight > 0)
      mount.setInternalRect(
        roundItemSizePx(liveWidth),
        roundItemSizePx(liveHeight),
      )
  }, [editing, dragging, resizeHandle, liveWidth, liveHeight])
  useEffect(() => {
    if (!editing) dragHandleRef.current = null
    releasedHandleRef.current = null
  }, [editing])

  // The canvas writes the fit once the person's hand has set the frame (a
  // handle release, a width or height typed into the drawer — the item's
  // frame differing from the document's, `authorFramed`): a scale the
  // read-only paint found for a box at rest or selected becomes its
  // authored sizes in the draft, so what is painted is the authored size and
  // the page is dirty until saved — only when a length actually moves. A
  // draft alone does not write: a mode or a maximum written into the draft,
  // a case value, a theme or a face refits the paint and keeps the scale on
  // it, and the sizes move only under the person's own gesture. The rewritten
  // document paints again, and that fit lands inside the write threshold.
  // The fit's scale is a ratio over the sizes it measured, so it is written
  // into the document that was painted and into no other: the read-only
  // paint is fitted twice (once at commit, once a frame later for the live
  // frame), the first fit's write lands between the two, and the second
  // fit's ratio applied to the written draft would compound it — 60 pt to
  // 15 to 3.7. A ratio for a superseded document is dropped; the repaint
  // of the written draft fits and writes for itself.
  // The document the write lands on is the one in the drafts atom at the
  // moment of the write, read inside the updater — never the one a snapshot
  // held when the fit ran, since a commit from the editing host can land in
  // between and must win. The frame the fit measured against is the live
  // one, so the written draft carries it too.
  const writeFitIntoDraft = useRecoilCallback(
    ({ snapshot, set }) =>
      async (scale: number, measured: EditorTextBoxDocument | null) => {
        if (!editorFitWritesSizes(scale)) return
        const pageResults = await snapshot.getPromise(
          editorTextBoxDocumentsSelector(pageId),
        )
        const pageDocument = pageResults?.get(id)?.document ?? null
        const frame = {
          width: liveWidthRef.current,
          height: liveHeightRef.current,
        }
        let wrote = false
        set(editorTextBoxDraftsAtom, (prev) => {
          const current = prev[id] ?? pageDocument
          if (!current) return prev
          if (measured !== null && current.box !== measured.box) return prev
          let framed = current
          if (
            frame.width > 0 &&
            frame.height > 0 &&
            (current.box.width !== frame.width ||
              current.box.height !== frame.height)
          ) {
            try {
              framed = resolveEditorTextBoxDocument({
                anchor: current.anchor,
                revision: current.revision,
                box: { ...current.box, ...frame },
                context: current.context,
              })
            } catch {
              framed = current
            }
          }
          const scaled = scaleEditorTextBoxDocumentSizes(framed, scale)
          if (scaled === current) return prev
          wrote = true
          return { ...prev, [id]: scaled }
        })
        if (wrote) set(unsavedChangesAtom, true)
      },
    [id, pageId],
  )

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    const mount = mountRef.current
    if (!mount || !isFocusExit(e)) return
    commitDraft(mount)
  }

  // The item wrapper already sits at the box's row position, so the box
  // paints at the wrapper's origin; the row's own x/y would offset it twice.
  const rendered = useMemo<EditorRenderedTextBox | null>(() => {
    if (textBoxDocument === null) return null
    const { rect } = textBoxDocument.resolved
    try {
      return renderEditorTextBox(textBoxDocument, {
        rect: { x: 0, y: 0, width: rect.width, height: rect.height },
      })
    } catch {
      return null
    }
  }, [textBoxDocument])
  const empty =
    textBoxDocument !== null && isEmptyEditorTextBoxDocument(textBoxDocument)
  // The seeded words a person never replaced: a placeholder, not content.
  // The canvas shows it so it can be found and deleted; the preview and the
  // PDF leave it out, and it never warns about a margin.
  const placeholderOnly =
    textBoxDocument !== null &&
    isPlaceholderOnlyEditorTextBoxDocument(textBoxDocument)

  const [inkRect, setInkRect] = useState<EditorInkRect | null>(null)
  const publishInkRect = useCallback(
    (next: EditorInkRect | null | undefined) => {
      const value = next ?? null
      setInkRect((prev) => {
        if (prev === value) return prev
        if (
          prev &&
          value &&
          prev.top === value.top &&
          prev.left === value.left &&
          prev.bottom === value.bottom &&
          prev.right === value.right
        )
          return prev
        return value
      })
    },
    [],
  )
  const [inkClusters, setInkClusters] = useState<EditorInkClusterBox[] | null>(
    null,
  )
  const publishInkClusters = useCallback(
    (next: EditorInkClusterBox[] | null | undefined) => {
      const value = next ?? null
      setInkClusters((prev) => {
        if (prev === value) return prev
        if (
          prev &&
          value &&
          prev.length === value.length &&
          prev.every(
            (c, i) =>
              c.char === value[i].char &&
              c.top === value[i].top &&
              c.left === value[i].left &&
              c.bottom === value[i].bottom &&
              c.right === value[i].right,
          )
        )
          return prev
        return value
      })
    },
    [],
  )
  const [liveEmpty, setLiveEmpty] = useState<boolean | null>(null)
  const [liveFieldCount, setLiveFieldCount] = useState<number | null>(null)
  const contentEmpty = editing ? (liveEmpty ?? empty) : empty || placeholderOnly
  // A box this session inserted from the toolbar and has not yet painted
  // (`editorTextBoxSeedIdsAtom`) is drawn at the seed height, shorter than
  // its one line of text, and its first paint raises the frame to the text
  // (`textBoxSeedFrameHeightPx`) instead of scaling the text down into the
  // seed. Nothing else is a seed: a saved row opens at its saved frame
  // however short, on every surface. Returns true when the frame moved and
  // the fit's scale must not be written. The id leaves the set with its
  // first paint under loaded faces, raised or not, so the judgment is made
  // once.
  const [seedIds, setSeedIds] = useRecoilState(editorTextBoxSeedIdsAtom)
  const seeded = isEditor && seedIds[id] === true
  const settleSeedFrame = useCallback(
    (textHeightPx: number | null): boolean => {
      if (!seeded) return false
      const next = textBoxSeedFrameHeightPx({
        itemHeightPx: typeof height === 'number' ? height : null,
        liveHeightPx: liveHeight,
        textHeightPx,
      })
      setSeedIds((prev) => {
        if (prev[id] === undefined) return prev
        const rest = { ...prev }
        delete rest[id]
        return rest
      })
      if (next === null) return false
      setLiveHeight?.(next)
      setItemHeight?.(next)
      return true
    },
    [seeded, id, height, liveHeight, setLiveHeight, setItemHeight, setSeedIds],
  )
  // The room the page leaves under this box, the same number on the canvas,
  // the preview and the PDF because all three read the same page
  // (`docs/lotus/sizing.md`, "Load"). A measurement for the flag, the log
  // and the job record: no surface applies it to the frame, which is the
  // row's on every surface. Read once per fit, never subscribed to, so a
  // drag never recomputes it. A surface that cannot say what is under the
  // box gets null and records nothing.
  const growthCeilingFor = useEditorTextBoxGrowthCeiling(pageGeometry)
  // The whole result, not the number alone: `boundedBy` names the item, the
  // margin or the fold that bounds the room, which is what the surfaces
  // stamp and the worker asserts on. It is measured from the item's frame —
  // the row's — with one line of the box's text, so a stored frame past its
  // room is flagged `preexisting` with the room, what bounds it and the
  // line, on the canvas as on the preview and the proof.
  const lineHeightPx =
    textBoxDocument === null
      ? null
      : editorTextBoxLineHeightPx(textBoxDocument.resolved)
  const readGrowthCeiling = useCallback((): EditorGrowthCeiling | null => {
    if (!(liveWidthRef.current > 0 && liveHeightRef.current > 0)) return null
    if (x == null || y == null) return null
    return growthCeilingFor({
      id,
      x,
      y,
      width: liveWidthRef.current,
      height: typeof height === 'number' ? height : liveHeightRef.current,
      lineHeight: lineHeightPx,
    })
  }, [growthCeilingFor, id, x, y, height, lineHeightPx])
  // The load result on the box root, in the same attributes and the same
  // spellings a TinyMCE box uses (`textGeometryAttributes.ts`): one
  // vocabulary for both kinds, so the probe and the worker's job record read
  // a Lotus box the way they read any other. The frame the fit fitted into,
  // the text's height at its authored sizes (the frame that would hold it at
  // fit scale 1), the room and what bounds it, and the text sizing the fit
  // ran under — the mode and the maximum size — so a surface that fitted
  // under the wrong mode is told apart from one that fitted right. Written on
  // every read-only fit, which is where a load result is read; the editing
  // host never sets them, because the frame under the author's hand is not
  // judged against the page.
  const stampLoadResult = useCallback(
    (
      host: HTMLElement,
      frameHeightPx: number,
      textHeightPx: number | null,
      ceiling: EditorGrowthCeiling | null,
      sizing: EditorTextSizing,
    ) => {
      const root = boxRootOf(host)
      if (!root) return
      root.setAttribute(TEXT_SIZING_ATTR, sizing.mode)
      root.setAttribute(
        TEXT_MAX_SIZE_PT_ATTR,
        sizing.maxFontSizePt === null
          ? TEXT_BOUNDED_BY_NONE
          : String(sizing.maxFontSizePt),
      )
      root.setAttribute(TEXT_LOAD_HEIGHT_ATTR, String(frameHeightPx))
      if (textHeightPx === null) root.removeAttribute(TEXT_CONTENT_HEIGHT_ATTR)
      else root.setAttribute(TEXT_CONTENT_HEIGHT_ATTR, String(textHeightPx))
      root.setAttribute(
        TEXT_CEILING_ATTR,
        ceiling === null ? TEXT_BOUNDED_BY_NONE : String(ceiling.maxHeight),
      )
      root.setAttribute(
        TEXT_BOUNDED_BY_ATTR,
        ceiling?.boundedBy ?? TEXT_BOUNDED_BY_NONE,
      )
      if (ceiling)
        root.setAttribute(
          TEXT_CEILING_FROM_ATTR,
          itemRectAttribute(
            ceiling.measuredFrom.x,
            ceiling.measuredFrom.y,
            ceiling.measuredFrom.width,
            null,
          ),
        )
      else root.removeAttribute(TEXT_CEILING_FROM_ATTR)
      stampKeptCeiling(root, ceiling?.kept ?? null)
    },
    [],
  )
  // What the load fit did to this box, for the overlay's control state and
  // the preview report. `fitted` is the text standing taller than the row's
  // frame at its authored sizes — the text was made smaller to fit — and not
  // `scale < 1`, which a word wider than the frame also causes. `kept` is
  // the room a stored frame past it was saved past.
  const [loadResultForWarning, setLoadResultForWarning] =
    useState<LoadResultForWarning | null>(null)
  const settleLoadResult = useCallback(
    (
      frameHeightPx: number,
      textHeightPx: number | null,
      ceiling: EditorGrowthCeiling | null,
      overflowing: boolean,
    ) => {
      const fitted =
        textHeightPx !== null &&
        Number.isFinite(textHeightPx) &&
        frameHeightPx + EDITOR_FIT_HEIGHT_TOL_PX < textHeightPx
      const boundedBy = ceiling?.boundedBy ?? null
      const kept = ceiling?.kept ?? null
      setLoadResultForWarning((prev) => {
        if (!prev) return { fitted, boundedBy, overflowing, kept }
        const prevKept = prev.kept ?? null
        const sameKept =
          prevKept === kept ||
          (prevKept !== null &&
            kept !== null &&
            prevKept.ceiling === kept.ceiling &&
            prevKept.boundedBy === kept.boundedBy &&
            prevKept.lineHeight === kept.lineHeight)
        return prev.fitted === fitted &&
          prev.boundedBy === boundedBy &&
          prev.overflowing === overflowing &&
          sameKept
          ? prev
          : { fitted, boundedBy, overflowing, kept }
      })
      return { fitted, boundedBy, kept }
    },
    [],
  )
  // Whether this box's read-only fits are load fits — paint only, nothing
  // written — or the author's: on the canvas a fit is a load fit only while
  // nothing of the author's has touched the frame — no draft of this box,
  // the item's frame still the document's, no handle held. The item's frame
  // differing from the document's is the author's hand (a handle release, a
  // size typed into the drawer), and the fit into the frame they set writes
  // (`writeFitIntoDraft`). Off the canvas every fit is a load fit. Every fit
  // runs under the box's text sizing, into the row's frame or the author's;
  // they differ in the write and in the load result the load fit records.
  const authorFramed =
    isEditor &&
    textBoxDocument !== null &&
    ((typeof width === 'number' &&
      Math.abs(width - textBoxDocument.box.width) > 0.5) ||
      (typeof height === 'number' &&
        Math.abs(height - textBoxDocument.box.height) > 0.5))
  const authorFramedRef = useRef(authorFramed)
  authorFramedRef.current = authorFramed
  const underLoadRule =
    !isEditor || (draft === null && !authorFramed && !dragging)
  const underLoadRuleRef = useRef(underLoadRule)
  underLoadRuleRef.current = underLoadRule
  // The one log line a load result leaves (`textBoxLoadLog.ts`): the frame
  // it painted against the frame the row holds, the scale the fit found under
  // the box's text sizing, and whether the text was made smaller or
  // overflows at the floor. Once per distinct result.
  const loggedLoadRef = useRef<string | null>(null)
  const logLoadResult = useCallback(
    (
      frameHeightPx: number,
      ceiling: EditorGrowthCeiling | null,
      fitted: boolean,
      overflowing: boolean,
      scale: number,
    ) => {
      if (textBoxDocument === null) return
      const sizing = editorTextSizingOf(textBoxDocument.box)
      const key = `${frameHeightPx}:${fitted}:${overflowing}:${ceiling?.boundedBy ?? ''}:${scale.toFixed(3)}:${sizing.mode}:${sizing.maxFontSizePt ?? ''}`
      if (loggedLoadRef.current === key) return
      loggedLoadRef.current = key
      logTextBoxLoadResult({
        itemId: id,
        kind: 'lotus',
        surface: isEditor ? 'editor' : (environment ?? 'preview'),
        storedHeight: textBoxDocument.box.height,
        paintedHeight: frameHeightPx,
        ceiling: ceiling?.maxHeight ?? null,
        boundedBy: ceiling?.boundedBy ?? null,
        fitted,
        overflowing,
        kept: ceiling?.kept ?? null,
        scale,
        sizing: sizing.mode,
        maxFontSizePt: sizing.maxFontSizePt,
      })
    },
    [environment, id, isEditor, textBoxDocument],
  )
  const documentFieldCount = useMemo(
    () =>
      textBoxDocument === null
        ? 0
        : textBoxDocument.box.blocks.reduce(
            (total, block) =>
              total + block.runs.filter((run) => run.kind === 'field').length,
            0,
          ),
    [textBoxDocument],
  )
  const fieldCount = editing
    ? (liveFieldCount ?? documentFieldCount)
    : documentFieldCount
  const inkDebug = useMemo(() => isEditor && readInkDebugFlag(), [isEditor])
  useEffect(() => {
    if (!editing) {
      setLiveEmpty(null)
      setLiveFieldCount(null)
      return undefined
    }
    const read = () => {
      publishInkRect(mountRef.current?.inkRect())
      publishInkClusters(mountRef.current?.inkClusters())
      const live = mountRef.current?.isEmpty()
      setLiveEmpty((prev) => (prev === live ? prev : (live ?? null)))
      const fields = mountRef.current?.fieldRunCount()
      setLiveFieldCount((prev) => (prev === fields ? prev : (fields ?? null)))
      readFieldOutlines()
    }
    read()
    const timer = setInterval(read, 250)
    return () => clearInterval(timer)
  }, [editing, publishInkRect, publishInkClusters, readFieldOutlines])
  const {
    showWarning: showBleedWarning,
    warningMessage: bleedWarningMessage,
    loadWarning,
  } = useBleedWarning({
    itemX: controlledX ?? x ?? 0,
    itemY: controlledY ?? y ?? 0,
    itemType: EDITOR_TEXT_BOX_ITEM_TYPE,
    inkRect,
    contentEmpty,
    loadResult: loadResultForWarning,
  })
  const overlayControlState = useMemo(
    () => ({
      text: {
        canSwapServiceTemplate: true,
        // The freeze needs an editing session, and a box only enters one as
        // the sole selection, so a multi-select offers no icon rather than an
        // icon that does nothing.
        hasTextCaseDetailAttribute: fieldCount > 0 && soleSelection,
      },
      warnings: {
        showBleedWarning,
        bleedWarningMessage,
        // The sentence belongs to the canvas's control state, where the
        // overlay paints no badge for it (`WarningIndicators.tsx`); the
        // preview and the PDF report a fitted box through the job record.
        loadWarningMessage: isEditor ? (loadWarning?.message ?? null) : null,
        loadWarningKey: isEditor ? (loadWarning?.key ?? null) : null,
      },
    }),
    [
      fieldCount,
      soleSelection,
      showBleedWarning,
      bleedWarningMessage,
      isEditor,
      loadWarning,
    ],
  )
  useItemControlState(id, overlayControlState)

  useLayoutEffect(() => {
    if (rendered === null) return
    ensureEditorTextBoxStructuralSheet()
    // The live editor adds its own classes on every paint. Nothing is ever
    // removed on unmount: the document's resolved sheet only grows, so a
    // delete or a page turn never touches the stylesheets the page's faces
    // resolve through.
    if (!editing) ensureEditorTextBoxResolvedStyles(rendered.resolvedClasses)
  }, [rendered, editing])

  const readonlyFitRafRef = useRef<number | null>(null)
  const fitVersionRef = useRef(0)
  // The scale the resting paint holds, with the document it was found for:
  // while a handle is held, and in the fit into the frame a release or the
  // drawer set, the search keeps this scale as its top so the text keeps the
  // size it was painted at under the person's hand, whether that size is
  // its authored one or a residual a load fit left on the paint. A fit of
  // another document — the fold the release wrote landing — starts from 1.
  const restingScaleRef = useRef<{
    document: EditorTextBoxDocument | null
    scale: number
  }>({ document: null, scale: 1 })
  // The frame the last resting fit fitted into: a frame that differs from it
  // while the item's frame is not the document's is one the person's hand
  // set through the drawer, fitted like a release.
  const restingFrameRef = useRef<{ width: number; height: number } | null>(null)
  // A fit ran while a handle was held, so the next fit at rest is the release's.
  const heldFitRef = useRef(false)
  // The frame the held handle started from, for the height the handle owns.
  const dragStartRef = useRef<{ width: number; height: number } | null>(null)
  const handFitOptions = useCallback(
    (
      liveFrame: { width: number; height: number },
      held: boolean,
    ): { growth: true; ceiling: number } | null => {
      if (EDITOR_TEXT_SCALING_ENABLED || !isEditor) return null
      const host = readonlyHostRef.current
      if (host !== null && !editorTextBoxFrameFollowsText(host)) return null
      const resting = restingFrameRef.current
      const handFramed =
        authorFramedRef.current &&
        resting !== null &&
        (resting.width !== liveFrame.width ||
          resting.height !== liveFrame.height)
      if (!held && !heldFitRef.current && !handFramed) return null
      const known = restingScaleRef.current
      return {
        growth: true,
        ceiling:
          known.document !== null && known.document === textBoxDocument
            ? Math.min(known.scale, 1)
            : 1,
      }
    },
    [isEditor, textBoxDocument],
  )
  const publishTextNeed = useCallback(
    (paintedHeightPx: number | null) => {
      if (editorTextBoxTextNeedRef)
        editorTextBoxTextNeedRef.current = paintedHeightPx
    },
    [editorTextBoxTextNeedRef],
  )
  // A held handle never takes the frame under the text, and gives the room
  // back once the text no longer needs it: the frame stands at the larger of
  // the handle's own height — the pointer's for a corner or a top or bottom
  // pill, the drag-start height for a side pill — and the height the paint
  // needs at the width in hand. The live size alone moves; the release
  // commits it.
  const settleHeldFrame = useCallback(
    (host: HTMLElement, paintedHeightPx: number | null) => {
      const start = dragStartRef.current
      if (start === null) return
      const liveH = liveHeightRef.current
      const next = editorHeldFrameHeight({
        ownPx:
          resizeHandle === 'e' || resizeHandle === 'w' ? start.height : liveH,
        paintedHeightPx,
        followsText: editorTextBoxFrameFollowsText(host),
      })
      if (next !== null && next !== liveH && next > 0) setLiveHeight?.(next)
    },
    [resizeHandle, setLiveHeight],
  )
  // A frame the person's hand set — a release, a width or height from the
  // drawer — never stands under the text: it rises to the height the paint
  // needs, and the page holds it from there.
  const holdFrameAboveText = useCallback(
    (paintedHeightPx: number | null) => {
      if (
        paintedHeightPx === null ||
        !Number.isFinite(paintedHeightPx) ||
        paintedHeightPx <= 0
      )
        return
      const need = Math.ceil(paintedHeightPx - 1e-6)
      if (need <= liveHeightRef.current + EDITOR_FIT_HEIGHT_TOL_PX) return
      applyFrameFromModel(
        liveWidthRef.current,
        need,
        releasedHandleRef.current ?? undefined,
      )
    },
    [applyFrameFromModel],
  )
  const boxRootOf = (host: HTMLElement): HTMLElement | null =>
    host.closest<HTMLElement>(`[${EDITOR_TEXT_BOX_ATTR}]`)
  const isPdf = environment === 'pdf'
  const stampFit = useCallback(
    (host: HTMLElement) => {
      const root = boxRootOf(host)
      if (!root) return
      fitVersionRef.current += 1
      stampEditorTextFit(root, host, {
        version: fitVersionRef.current,
        fitPending: readonlyFitRafRef.current != null,
        pdf: isPdf,
      })
    },
    [isPdf],
  )

  // A first measure can land on fallback faces, and the real face changes
  // the measured extent: the box's own settle catches loads already pending,
  // and starts again while a face is still loading after its time limit, so
  // a face that lands late refits the box whatever else the page still
  // loads; the trigger catches loads that start later.
  useLayoutEffect(() => {
    if (editing || rendered === null || dragging) return undefined
    let cancelled = false
    const fit = () => {
      if (cancelled || !readonlyHostRef.current) return
      countEditorProbeRender(`readonlyLayoutFit:${id.slice(0, 8)}`)
      // The rendered box carries the saved frame; the page's live frame is
      // the truth between a handle release and the save that flushes it, and
      // a fit that wrote sizes for the saved frame would fight the fit that
      // follows under the live one.
      const boxEl = readonlyHostRef.current.querySelector<HTMLElement>(
        EDITOR_TEXT_BOX_SELECTOR,
      )
      if (boxEl && liveWidthRef.current > 0 && liveHeightRef.current > 0) {
        boxEl.style.width = `${liveWidthRef.current}px`
        boxEl.style.height = `${liveHeightRef.current}px`
      }
      const loadRule = underLoadRuleRef.current
      const ceiling = loadRule ? readGrowthCeiling() : null
      const sizing = editorTextSizingOf(textBoxDocument?.box ?? {})
      const hand = handFitOptions(
        { width: liveWidthRef.current, height: liveHeightRef.current },
        false,
      )
      const result = fitRenderedEditorTextBox(readonlyHostRef.current, {
        resolvedClasses: rendered.resolvedClasses,
        sizing,
        ...(hand ?? {}),
      })
      publishTextNeed(result.paintedHeightPx)
      if (hand) holdFrameAboveText(result.paintedHeightPx)
      else
        restingScaleRef.current = {
          document: textBoxDocument,
          scale: result.scale,
        }
      stampLoadResult(
        readonlyHostRef.current,
        result.frameHeightPx,
        result.textHeightPx,
        ceiling,
        sizing,
      )
      const loadResult = settleLoadResult(
        result.frameHeightPx,
        result.textHeightPx,
        ceiling,
        result.overflowing,
      )
      publishInkRect(result.inkRect)
      publishInkClusters(result.inkClusters)
      stampFit(readonlyHostRef.current)
      // A fit under a face still loading measured a fallback: the refit
      // the face's arrival triggers is the one that writes.
      const facesLoaded = editorTextBoxFacesLoaded(readonlyHostRef.current)
      if (facesLoaded && textBoxDocument !== null)
        rememberEditorTextBoxInkRect(textBoxDocument, result.inkRect ?? null)
      if (facesLoaded && bleedReport && textBoxDocument !== null)
        reportPreviewBleedWarning(
          id,
          result.inkRect ?? null,
          { x: textBoxDocument.box.x, y: textBoxDocument.box.y },
          bleedReport,
        )
      if (facesLoaded && bleedReport)
        reportPreviewLoadWarning(id, 'lotus', loadResult, bleedReport)
      if (facesLoaded && loadRule)
        logLoadResult(
          result.frameHeightPx,
          ceiling,
          loadResult.fitted,
          result.overflowing,
          result.scale,
        )
      // A load fit is a paint: the frame is the row's and the scale stays on
      // the paint, nothing to the draft. A fit into a frame the person's hand
      // set writes, as a release does; a draft alone keeps the scale on the
      // paint. A seed's first paint raises the frame instead and writes
      // nothing for that pass. A fit measured in the fallback of a face that
      // failed writes nothing: a print paints the real face.
      if (
        isEditor &&
        editorTextBoxFacesWriteSizes(readonlyHostRef.current) &&
        !settleSeedFrame(result.textHeightPx) &&
        authorFramedRef.current
      )
        void writeFitIntoDraft(result.scale, textBoxDocument)
    }
    fit()
    const uninstallFaceRefit = readonlyHostRef.current
      ? installEditorBoxFaceRefit(readonlyHostRef.current, fit)
      : () => {}
    const uninstallFontRefit =
      typeof document !== 'undefined'
        ? installEditorFontRefitTrigger(
            document,
            fit,
            readonlyHostRef.current ?? undefined,
          )
        : () => {}
    return () => {
      cancelled = true
      uninstallFaceRefit()
      uninstallFontRefit()
    }
  }, [
    rendered,
    textBoxDocument,
    editing,
    dragging,
    id,
    isEditor,
    bleedReport,
    publishInkRect,
    publishInkClusters,
    stampFit,
    settleSeedFrame,
    readGrowthCeiling,
    stampLoadResult,
    settleLoadResult,
    logLoadResult,
    writeFitIntoDraft,
    handFitOptions,
    publishTextNeed,
    holdFrameAboveText,
  ])

  // The read-only paint under the page's frame: a full fit into it that
  // writes. With text scaling on: while a pill is held, the same fit on the
  // paint alone; while a corner is held, no fit — authored × k until the
  // release fits. With it off: while any handle is held, and in the fit
  // into the frame the release or the drawer set, the fit keeps the paint's
  // scale and reports the height the text needs, the frame rises to it,
  // and the release folds that scale into the draft.
  useLayoutEffect(() => {
    if (editing || rendered === null) return undefined
    if (dragging) {
      releasedHandleRef.current = null
      if (dragStartRef.current === null)
        dragStartRef.current = { width: liveWidth, height: liveHeight }
      heldFitRef.current = true
    } else {
      if (dragStartRef.current !== null)
        releasedHandleRef.current = resizeHandle ?? null
      dragStartRef.current = null
    }
    if (readonlyFitRafRef.current != null)
      cancelAnimationFrame(readonlyFitRafRef.current)
    if (readonlyHostRef.current)
      boxRootOf(readonlyHostRef.current)?.removeAttribute(
        EDITOR_TEXT_FIT_SETTLED_ATTR,
      )
    readonlyFitRafRef.current = requestAnimationFrame(() => {
      readonlyFitRafRef.current = null
      const host = readonlyHostRef.current
      if (!host) return
      const boxEl = host.querySelector<HTMLElement>(EDITOR_TEXT_BOX_SELECTOR)
      if (boxEl && liveWidth > 0 && liveHeight > 0) {
        boxEl.style.width = `${liveWidth}px`
        boxEl.style.height = `${liveHeight}px`
      }
      if (
        EDITOR_TEXT_SCALING_ENABLED &&
        dragging &&
        isEditorCornerHandle(resizeHandle)
      ) {
        const cornerScale = (width ?? 0) > 0 ? liveWidth / (width as number) : 1
        if (cornerScale !== 1)
          previewRenderedEditorTextBoxCornerScale(host, cornerScale)
        countEditorProbeRender(
          `readonlyDrag:${id.slice(0, 8)}:handle=${resizeHandle}:k=${cornerScale.toFixed(3)}`,
        )
        return
      }
      // A pill held on a selected box fits the paint into the moving frame
      // on every frame the page hands it, and writes nothing until the
      // release lands here without `dragging`. A load fit ends with the
      // first handle held: the release fits the text into the frame the
      // author set and writes.
      const loadRule = underLoadRuleRef.current
      const ceiling = loadRule ? readGrowthCeiling() : null
      const sizing = editorTextSizingOf(textBoxDocument?.box ?? {})
      const hand = handFitOptions(
        { width: liveWidth, height: liveHeight },
        dragging,
      )
      const result = fitRenderedEditorTextBox(host, {
        resolvedClasses: rendered.resolvedClasses,
        sizing,
        ...(hand ?? {}),
      })
      publishTextNeed(result.paintedHeightPx)
      if (hand && dragging) settleHeldFrame(host, result.paintedHeightPx)
      else if (hand) holdFrameAboveText(result.paintedHeightPx)
      releasedHandleRef.current = null
      if (!dragging) {
        heldFitRef.current = false
        restingFrameRef.current = { width: liveWidth, height: liveHeight }
        if (!hand)
          restingScaleRef.current = {
            document: textBoxDocument,
            scale: result.scale,
          }
      }
      stampLoadResult(
        host,
        result.frameHeightPx,
        result.textHeightPx,
        ceiling,
        sizing,
      )
      const loadResult = settleLoadResult(
        result.frameHeightPx,
        result.textHeightPx,
        ceiling,
        result.overflowing,
      )
      publishInkRect(result.inkRect)
      publishInkClusters(result.inkClusters)
      countEditorProbeRender(
        `readonlyFit:${id.slice(0, 8)}:live=${liveHeight}:scale=${result.scale.toFixed(3)}${dragging ? `:drag=${resizeHandle}` : ''}${loadRule ? ':load' : ''}`,
      )
      if (dragging) return
      stampFit(host)
      const facesLoaded = editorTextBoxFacesLoaded(host)
      if (facesLoaded && loadRule)
        logLoadResult(
          result.frameHeightPx,
          ceiling,
          loadResult.fitted,
          result.overflowing,
          result.scale,
        )
      // A fit measured in the fallback of a face that failed writes nothing,
      // as the load fit's write above.
      if (
        isEditor &&
        editorTextBoxFacesWriteSizes(host) &&
        !settleSeedFrame(result.textHeightPx) &&
        authorFramedRef.current
      )
        void writeFitIntoDraft(result.scale, textBoxDocument)
    })
    return () => {
      if (readonlyFitRafRef.current != null) {
        cancelAnimationFrame(readonlyFitRafRef.current)
        readonlyFitRafRef.current = null
      }
    }
  }, [
    editing,
    rendered,
    textBoxDocument,
    liveWidth,
    liveHeight,
    dragging,
    resizeHandle,
    width,
    id,
    isEditor,
    publishInkRect,
    publishInkClusters,
    stampFit,
    settleSeedFrame,
    readGrowthCeiling,
    stampLoadResult,
    settleLoadResult,
    logLoadResult,
    writeFitIntoDraft,
    handFitOptions,
    publishTextNeed,
    settleHeldFrame,
    holdFrameAboveText,
  ])

  const loading = results === null
  const failure =
    loading || rendered !== null
      ? null
      : (result?.error ?? 'the page holds no rows for this box')
  useEffect(() => {
    if (failure !== null)
      console.error(`PageEditorTextBox: box ${id} cannot render — ${failure}`)
  }, [id, failure])

  // Off the canvas a placeholder-only box has no element at all: nothing to
  // paint, nothing for the proof to wait on, nothing to measure.
  if (!isEditor && placeholderOnly) return null

  return (
    <S.EditorTextBox
      ref={itemRef}
      data-editor-text-box={id}
      data-editor-text-editing={editing ? 'true' : undefined}
      data-text-loading={loading ? 'true' : undefined}
      data-item-rect={itemRectAttribute(x, y, width, height)}
      environment={environment}
      full={full}
      print={print}
      x={x}
      y={y}
      z={z}
      width={width}
      height={height}
      edits={edits}
      onBlur={editing ? handleBlur : undefined}
      onDoubleClick={isEditor ? handleDoubleClick : undefined}
      onMouseOver={editing ? handleEditingMouseOver : undefined}
      onMouseLeave={editing ? clearFieldTooltip : undefined}
    >
      {isEditor && showServiceTemplateModal && (
        <ServiceTemplateCreateModal
          show={showServiceTemplateModal}
          setShow={setShowServiceTemplateModal}
          templateName={serviceTemplateName}
          setTemplateName={setServiceTemplateName}
          onCreateTemplate={handleCreateServiceTemplate}
        />
      )}
      {isEditor && confirmingMakeEditable && (
        <MakeEditableModal
          onCancel={cancelMakeEditable}
          onConfirm={confirmMakeEditable}
        />
      )}
      {editing && !mountFailed ? (
        <>
          <EditorTextBoxToolbar
            getMount={getMount}
            getHostEl={getHostEl}
            subscribeRefit={subscribeRefit}
            onHoldSelection={setHeldSelection}
            themeColors={themeColors}
            onPulseRoleTarget={pulseRoleTarget}
            onConfigureField={openConfigureField}
            isAdmin={isAdmin}
            caseData={modalCaseData}
            onAuthorRole={openRoleModal}
            scanUsedFonts={scanUsedFonts}
            onInsertRule={insertRuleAtCaret}
          />
          <HeldSelectionLayer rects={heldSelection} />
          <ProtectPulseLayer pulse={protectPulse} />
          <FieldOutlineLayer outlines={fieldOutlines} />
          <FieldTooltipPortal tooltip={fieldTooltip} />
          {inkDebug && <InkDebugLayer clusters={inkClusters} />}
          <div
            ref={hostElRef}
            data-editor-text-host={id}
            className="editor-text-host"
            style={{
              width: '100%',
              height: '100%',
              position: 'relative',
              zIndex: 1,
            }}
          />
          {/* A CSS transform on an ancestor makes position: fixed resolve
              against that ancestor, so the rail portals to the body to
              escape the canvas zoom. */}
          {sizingInspector &&
            createPortal(
              <EditorTextBoxSizingInspector
                boxId={id}
                inspection={inspection}
              />,
              document.body,
            )}
          {fieldModal &&
            (fieldModal.fieldKey === EDITOR_FIELD_NAME_OF_DECEASED ? (
              <DeceasedNameFieldModal
                getMount={getMount}
                getHostEl={getHostEl}
                caseData={modalCaseData}
                savedSel={fieldModal.savedSel}
                field={fieldModal.field}
                onAuthorValues={onAuthorValues}
                onClose={() => setFieldModal(null)}
              />
            ) : COMPOSITE_FIELD_KEYS.includes(fieldModal.fieldKey) ? (
              <CompositeFieldModal
                getMount={getMount}
                getHostEl={getHostEl}
                caseData={modalCaseData}
                savedSel={fieldModal.savedSel}
                fieldKey={fieldModal.fieldKey as CompositeFieldKey}
                serviceId={fieldModal.serviceId}
                field={fieldModal.field}
                onAuthorValues={onAuthorValues}
                dateSupport={dateSupport}
                onClose={() => setFieldModal(null)}
              />
            ) : tabularRowForm(fieldModal.fieldKey) ? (
              <TabularFieldModal
                getMount={getMount}
                getHostEl={getHostEl}
                caseRecord={caseDetails ?? {}}
                savedSel={fieldModal.savedSel}
                fieldKey={fieldModal.fieldKey}
                rowType={fieldModal.rowType}
                field={fieldModal.field}
                onAuthorRows={onAuthorRows}
                onClose={() => setFieldModal(null)}
              />
            ) : (
              <FieldInsertModal
                getMount={getMount}
                getHostEl={getHostEl}
                caseData={modalCaseData}
                savedSel={fieldModal.savedSel}
                fieldKey={fieldModal.fieldKey}
                field={fieldModal.field}
                onAuthorValue={onAuthorValue}
                dateSupport={dateSupport}
                onClose={() => setFieldModal(null)}
              />
            ))}
          {roleModal && (
            <ThemeRoleDecorationModal
              getMount={getMount}
              getHostEl={getHostEl}
              decorations={decorationLayers}
              initialRole={roleModal.initialRole}
              savedSel={roleModal.savedSel}
              onAuthorDecoration={onAuthorDecoration}
              onClose={() => setRoleModal(null)}
            />
          )}
        </>
      ) : (
        rendered !== null && (
          <>
            <div
              ref={readonlyHostRef}
              className="editor-text-host"
              style={{ width: '100%', height: '100%' }}
              dangerouslySetInnerHTML={{ __html: rendered.html }}
            />
            {inkDebug && <InkDebugLayer clusters={inkClusters} />}
          </>
        )
      )}
      {(failure !== null || (editing && mountFailed)) && (
        <div data-editor-text-error>Couldn’t load this text box</div>
      )}
      {isEditor && empty && !editing && rendered !== null && (
        <S.EmptyHint data-editor-text-empty-hint aria-hidden="true">
          Text box
        </S.EmptyHint>
      )}
    </S.EditorTextBox>
  )
}

export default PageEditorTextBox
